// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the performance lab (?qa=perf)
//  ---------------------------------------------------------------------------
//  WHY IT EXISTS. The staging livelock of 2026-08-31 burned CPU for days and
//  the diagnosis had to be dug out of a wrangler tail, because the app has no
//  reading of its own work. This panel is that reading: open the app with
//  `?qa=perf`, press Measure, and the account the browser is actually holding
//  is walked and timed — not a synthetic benchmark, YOUR keys, YOUR databases,
//  the same work the sync loop and the boot pay for on this very device.
//
//  WHAT IT MEASURES, and why these five and not others:
//
//    · the storage walk    — every `md:` key read raw once; the floor under
//                            every hydration and every sync cycle.
//    · sync composition    — one full `mdLocalDocs()`: what the push loop
//                            composes EVERY cycle before deciding to send
//                            nothing. The livelock lived here.
//    · the hash pass       — `mdHash` over every composed document; the other
//                            half of that same per-cycle bill.
//    · cold compose        — every database dropped from cache and read back,
//                            one row per database, so a heavy one is named
//                            rather than averaged away.
//    · the trash pass      — `mdTrashSyncDocs()`, the offer-side walk that
//                            joined the sync cycle with the deletion fix.
//
//  Long tasks ride along when the browser can count them (Chrome's `longtask`
//  buffer, observed from module load). Where it cannot — Safari, the test
//  runner — the panel says "not supported here" rather than printing a zero
//  that would read as innocence.
//
//  WHAT IT IS NOT. Not shipped UI — it mounts only behind `?qa=perf`, like
//  `?qa=db500` before it. Not a monitor — it measures when pressed, never on a
//  timer; a lab that polls would be the very load it claims to observe. And it
//  writes nothing: every reading is a read. The one cache it touches is the
//  database compose cache, which `mdDatabaseForgetAll` drops on purpose —
//  that is what makes the compose COLD, and the next reader simply rebuilds.
// ════════════════════════════════════════════════════════════════════════════

type MdPerfSection = { ms: number };
type MdPerfStorage = MdPerfSection & { keys: number; bytes: number };
type MdPerfDocs = MdPerfSection & { docs: number; bytes: number };
type MdPerfHash = MdPerfSection & { docs: number };
type MdPerfDatabase = { id: string; name: string; rows: number; views: number; ms: number };
type MdPerfTrash = MdPerfSection & { entries: number };
type MdPerfLongTasks = { count: number; totalMs: number } | null;
type MdPerfReport = {
  storage: MdPerfStorage; docs: MdPerfDocs; hash: MdPerfHash;
  databases: MdPerfDatabase[]; trash: MdPerfTrash; longTasks: MdPerfLongTasks;
};

(function (g: any) {
  'use strict';

  const doc = g.document as Document | undefined;

  /* ── long tasks, counted from module load ─────────────────────────────────
     The observer starts when the panel mounts, `buffered` so the boot's own
     blocking tasks — historically the worst offenders — are already in the
     reading. `longTasks` stays null wherever the browser cannot say, because a
     zero from a browser that was not counting is a lie with a chart. */
  let longCount = 0;
  let longTotal = 0;
  let observing = false;

  function observeLongTasks(): void {
    if (observing) return;
    const PO = g.PerformanceObserver;
    if (typeof PO !== 'function' || !PO.supportedEntryTypes?.includes('longtask')) return;
    try {
      const seen = new PO((list: PerformanceObserverEntryList) => {
        for (const entry of list.getEntries()) { longCount += 1; longTotal += entry.duration; }
      });
      seen.observe({ type: 'longtask', buffered: true });
      observing = true;
    } catch { /* an observer the browser lied about supporting: stay null */ }
  }

  const tick = (): number => (typeof g.performance?.now === 'function' ? g.performance.now() : Date.now());

  /* ── the measurement ──────────────────────────────────────────────────────
     Reads only. The account is walked the way the app itself walks it, through
     the same exported doors the sync loop uses, so the numbers are the app's
     numbers rather than a benchmark's. */
  async function mdPerfLabMeasure(): Promise<MdPerfReport> {
    let at = tick();
    let bytes = 0;
    const keys: string[] = g.mdStorageKeys?.('md:') ?? [];
    for (const key of keys) {
      const raw = g.mdReadRaw?.(key);
      bytes += key.length + (typeof raw === 'string' ? raw.length : 0);
    }
    const storage: MdPerfStorage = { keys: keys.length, bytes, ms: tick() - at };

    at = tick();
    const local: Record<string, unknown> = g.mdLocalDocs ? await g.mdLocalDocs() : {};
    const entries = Object.entries(local);
    let docBytes = 0;
    for (const [key, value] of entries) {
      docBytes += key.length + (value == null ? 0 : JSON.stringify(value).length);
    }
    const docs: MdPerfDocs = { docs: entries.length, bytes: docBytes, ms: tick() - at };

    at = tick();
    if (g.mdHash) for (const [, value] of entries) g.mdHash(value);
    const hash: MdPerfHash = { docs: g.mdHash ? entries.length : 0, ms: tick() - at };

    const databases: MdPerfDatabase[] = [];
    const listed: { id: string; name: string }[] = g.mdDatabaseList?.() ?? [];
    for (const { id, name } of listed) {
      at = tick();
      g.mdDatabaseForgetAll?.();
      const db = g.mdDatabaseRead?.(id);
      databases.push({
        id, name,
        rows: Array.isArray(db?.rows) ? db.rows.length : 0,
        views: Array.isArray(db?.views) ? db.views.length : 0,
        ms: tick() - at,
      });
    }

    at = tick();
    const held: Record<string, unknown> = g.mdTrashSyncDocs?.() ?? {};
    const trash: MdPerfTrash = { entries: Object.keys(held).length, ms: tick() - at };

    const longTasks: MdPerfLongTasks = observing ? { count: longCount, totalMs: longTotal } : null;

    const report: MdPerfReport = { storage, docs, hash, databases, trash, longTasks };
    render(report);
    return report;
  }

  /* ── the panel ────────────────────────────────────────────────────────────
     Plain DOM, deliberately: this mounts before React and must not depend on
     the app having managed to render — a lab that only opens when the app is
     healthy cannot examine an app that is not. Styled inline for the same
     reason, in the Manifestation palette. */
  const kb = (n: number): string => (n < 1024 ? `${n} B` : `${(n / 1024).toFixed(1)} KB`);
  const msOf = (n: number): string => `${n.toFixed(1)} ms`;

  function row(body: HTMLElement, label: string, work: string, time: string): void {
    const tr = doc!.createElement('tr');
    const th = doc!.createElement('th');
    th.setAttribute('scope', 'row');
    th.textContent = label;
    th.style.cssText = 'text-align:left;font-weight:500;padding:4px 12px 4px 0;color:#fff;';
    const work_ = doc!.createElement('td');
    work_.textContent = work;
    const time_ = doc!.createElement('td');
    time_.textContent = time;
    for (const td of [work_, time_]) {
      td.style.cssText = 'text-align:right;padding:4px 0 4px 12px;font-variant-numeric:tabular-nums;color:#cfc4f4;white-space:nowrap;';
    }
    tr.append(th, work_, time_);
    body.append(tr);
  }

  function render(report: MdPerfReport): void {
    const body = doc?.querySelector('[data-md-perf-lab] tbody') as HTMLElement | null;
    if (!body) return;
    body.textContent = '';
    row(body, 'Storage walk', `${report.storage.keys} keys · ${kb(report.storage.bytes)}`, msOf(report.storage.ms));
    row(body, 'Sync composition', `${report.docs.docs} docs · ${kb(report.docs.bytes)}`, msOf(report.docs.ms));
    row(body, 'Hash pass', `${report.hash.docs} docs`, msOf(report.hash.ms));
    for (const db of report.databases) {
      row(body, `Cold compose · ${db.name}`, `${db.rows} rows · ${db.views} views`, msOf(db.ms));
    }
    row(body, 'Trash pass', `${report.trash.entries} entries`, msOf(report.trash.ms));
    if (report.longTasks) {
      row(body, 'Long tasks since load', `${report.longTasks.count} over 50 ms`, msOf(report.longTasks.totalMs));
    } else {
      row(body, 'Long tasks since load', 'not supported here', '—');
    }
  }

  function mdPerfLabMount(): void {
    if (!doc?.body || doc.querySelector('[data-md-perf-lab]')) return;
    observeLongTasks();

    const panel = doc.createElement('section');
    panel.setAttribute('data-md-perf-lab', '');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Performance lab');
    /* Above every ledger plane (which top out near 140) and above the caret
       layer at 300 — a lab that opens under the thing it examines is useless. */
    panel.style.cssText = [
      'position:fixed', 'right:16px', 'bottom:16px', 'z-index:400',
      'width:min(360px, calc(100vw - 32px))', 'max-height:70vh', 'overflow:auto',
      'background:#0b0812', 'color:#fff', 'border:1px solid #6b4fd8',
      'border-radius:12px', 'padding:14px 16px', 'font-size:13px', 'line-height:1.5',
      'box-shadow:0 12px 40px rgba(0,0,0,0.5)',
    ].join(';');

    const head = doc.createElement('header');
    head.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:6px;';
    const title = doc.createElement('h2');
    title.textContent = 'Performance lab';
    title.style.cssText = 'font-size:14px;font-weight:600;margin:0;';
    const close = doc.createElement('button');
    close.setAttribute('data-md-perf-close', '');
    close.setAttribute('aria-label', 'Close the performance lab');
    close.textContent = '×';
    close.style.cssText = 'background:none;border:none;color:#cfc4f4;font-size:18px;line-height:1;cursor:pointer;padding:2px 6px;';
    close.addEventListener('click', () => panel.remove());
    head.append(title, close);

    const note = doc.createElement('p');
    note.textContent = 'Measures this device’s own account: the work sync and boot pay for, read live.';
    note.style.cssText = 'margin:0 0 10px;color:#a89ecb;';

    const measure = doc.createElement('button');
    measure.setAttribute('data-md-perf-measure', '');
    measure.textContent = 'Measure';
    measure.style.cssText = [
      'background:#6b4fd8', 'color:#fff', 'border:none', 'border-radius:8px',
      'padding:6px 14px', 'font-size:13px', 'font-weight:600', 'cursor:pointer', 'margin-bottom:10px',
    ].join(';');
    measure.addEventListener('click', () => {
      measure.disabled = true;
      measure.textContent = 'Measuring…';
      void mdPerfLabMeasure().finally(() => {
        measure.disabled = false;
        measure.textContent = 'Measure again';
      });
    });

    const table = doc.createElement('table');
    table.style.cssText = 'width:100%;border-collapse:collapse;';
    const caption = doc.createElement('caption');
    caption.textContent = 'What this device just paid for, and how long it took';
    caption.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);';
    const headRow = doc.createElement('thead');
    const tr = doc.createElement('tr');
    for (const name of ['Reading', 'Work', 'Time']) {
      const th = doc.createElement('th');
      th.setAttribute('scope', 'col');
      th.textContent = name;
      th.style.cssText = `text-align:${name === 'Reading' ? 'left' : 'right'};font-weight:600;color:#a89ecb;padding:0 0 6px;border-bottom:1px solid #2c2440;`;
      tr.append(th);
    }
    headRow.append(tr);
    const tbody = doc.createElement('tbody');
    table.append(caption, headRow, tbody);

    panel.append(head, note, measure, table);
    doc.body.append(panel);
  }

  function mdPerfLabUnmount(): void {
    doc?.querySelector('[data-md-perf-lab]')?.remove();
  }

  Object.assign(g, { mdPerfLabMount, mdPerfLabUnmount, mdPerfLabMeasure });

  if (typeof g.location?.search === 'string' && new URLSearchParams(g.location.search).get('qa') === 'perf') {
    mdPerfLabMount();
  }

  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) mod.exports = { mdPerfLabMount, mdPerfLabUnmount, mdPerfLabMeasure };
})(typeof window !== 'undefined' ? window : globalThis);
